import i18n from '@/utils/i18n';
import { BASE_URL as BASE_S3_URL } from '@/services/s3';
import {
  CatalogCategoryType,
  FilteredCategoryItemGroup,
  ItemListOption,
} from '@/contexts/item-context-types';
import { GTINFilters } from '@/hooks/use-gtin-metadata-filters';
import {
  ItemsContext_VariantsPackFieldsFragment,
  SearchCatalog_VariantPackFieldsFragment,
} from '@/types';
import { ListOption } from '@/components/list-item';

export const ALL_CATEGORIES_FILTER = i18n.t('all');
export const ALL_CATEGORY_VALUE = '__ALL__';
export const ALL_FILTER = {
  title: ALL_CATEGORIES_FILTER,
  value: ALL_CATEGORY_VALUE,
};

export const NULL_CATEGORY_VALUE = '__NULL__';

export interface CategoryListReturnType {
  fetchMoreOffset: number | null;
  fetchNextPage: () => Promise<void>;
  filtersAvailable: boolean;
  gtinFilters: GTINFilters;
  listData: ItemListOption[];
  loading: boolean;
  subcategoryItemCounts?: Map<string, number>;
}

export interface CategoryListOptionMetadata {
  count?: number;
}

export type CategoryListOption = ListOption<CategoryListOptionMetadata>;

export type CategoryCountsMap = Map<string, number>;
export type CategoryVariantPacksMap = Map<string, Set<string>>;

export const getCategoryImageURL = (
  selectedSupplierUUID: string,
  categoryName: string,
) => {
  const convertedCategoryName = categoryName
    .toLowerCase()
    .replace('/', '-')
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word characters (excluding -)
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-|-$/g, ''); // Trim leading/trailing -

  return `${BASE_S3_URL}/suppliers/${selectedSupplierUUID}/categories/${convertedCategoryName}.jpeg`;
};

export const isVariantPackInCategory = (
  variantPack: SearchCatalog_VariantPackFieldsFragment,
  category?: CatalogCategoryType,
) => {
  return (
    variantPack.item.restaurant_item_overrides[0]?.category ===
      category?.name ||
    (typeof variantPack.item.restaurant_item_overrides[0]?.category ===
      'undefined' &&
      variantPack.item.category === category?.name)
  );
};

export const isVariantPackInItemGroupCategory = (
  category: CatalogCategoryType,
  variantPack: SearchCatalog_VariantPackFieldsFragment,
  childItemGroupUUIDs?: string[],
) => {
  const allItemGroups = [category.uuid, ...(childItemGroupUUIDs ?? [])];
  const itemGroupUUIDSet = new Set(allItemGroups);

  // If the variant pack is not assigned to any group, it belongs to "Other"
  if (
    category.uuid === NULL_CATEGORY_VALUE &&
    (!variantPack.variant_pack_group_memberships ||
      variantPack.variant_pack_group_memberships.length === 0)
  ) {
    return true;
  }

  return variantPack.variant_pack_group_memberships?.some((membership) =>
    itemGroupUUIDSet.has(membership.variant_pack_group_uuid),
  );
};

export const findItemGroupPath = (
  itemGroups: FilteredCategoryItemGroup[],
  targetItemGroup: FilteredCategoryItemGroup,
  path: FilteredCategoryItemGroup[] = [],
): FilteredCategoryItemGroup[] | null => {
  for (const group of itemGroups) {
    if (group.path === targetItemGroup.path) {
      if (group.directSubcategories && group.directSubcategories.length > 0) {
        return [...path, group];
      }
      return path;
    }

    if (group.directSubcategories && group.directSubcategories.length > 0) {
      const foundPath = findItemGroupPath(
        group.directSubcategories,
        targetItemGroup,
        [...path, group],
      );

      if (foundPath) {
        return foundPath;
      }
    }
  }
  return null;
};

export const isPathInCategoryItemGroups = (
  categoryItemGroups: FilteredCategoryItemGroup[],
  filterPath: string,
): boolean => {
  return categoryItemGroups.some((s) => {
    return (
      s.path === filterPath ||
      isPathInCategoryItemGroups(s.directSubcategories ?? [], filterPath)
    );
  });
};

export const getVariantPackGroupParentUUIDs = (
  groupUUIDs: string[],
  parentMap: { [key: string]: string[] },
): string[] => {
  return groupUUIDs.reduce<string[]>((result, groupUUID) => {
    const parentUUIDs = parentMap[groupUUID] || [];
    result.push(groupUUID, ...parentUUIDs);
    return result;
  }, []);
};

/**
 * Returns a set of categories for a variant pack
 * @param variantPack - the variant pack to get the categories for
 * @param itemGroupsEnabled - whether item groups are enabled
 * @returns A set of categories the variant pack belongs to
 */
export const getCategoriesForVariantPack = (
  variantPack: ItemsContext_VariantsPackFieldsFragment,
  itemGroupsEnabled: boolean,
): Set<string> => {
  // Always include ALL category
  const categories: Set<string> = new Set([ALL_CATEGORY_VALUE]);

  if (itemGroupsEnabled) {
    if (variantPack.variant_pack_group_memberships?.length) {
      for (const m of variantPack.variant_pack_group_memberships) {
        categories.add(m.variant_pack_group_uuid);
      }
    } else {
      categories.add(NULL_CATEGORY_VALUE);
    }
  } else {
    categories.add(variantPack.item.category ?? NULL_CATEGORY_VALUE);
  }

  return categories;
};
